// Colour naming for option values (PRODUCT-001 swatches). A hex code typed as a value becomes the
// closest named colour, matched by CIEDE2000 (ΔE00, CIE 2001 / ISO/CIE 11664-6), the colour-difference
// formula the colour industry uses for perceived difference. Names: the CSS Color 4 named colours
// (W3C; "Grey" spelling, aliases dropped) plus common apparel and retail colour names.

const NAMES: Record<string, string> = {
  // CSS Color 4 named colours
  "Alice Blue": "#f0f8ff",
  "Antique White": "#faebd7",
  Aquamarine: "#7fffd4",
  Azure: "#f0ffff",
  Beige: "#f5f5dc",
  Bisque: "#ffe4c4",
  Black: "#000000",
  "Blanched Almond": "#ffebcd",
  Blue: "#0000ff",
  "Blue Violet": "#8a2be2",
  Brown: "#a52a2a",
  Burlywood: "#deb887",
  "Cadet Blue": "#5f9ea0",
  Chartreuse: "#7fff00",
  Chocolate: "#d2691e",
  Coral: "#ff7f50",
  "Cornflower Blue": "#6495ed",
  Cornsilk: "#fff8dc",
  Crimson: "#dc143c",
  Cyan: "#00ffff",
  "Dark Blue": "#00008b",
  "Dark Cyan": "#008b8b",
  "Dark Goldenrod": "#b8860b",
  "Dark Grey": "#a9a9a9",
  "Dark Green": "#006400",
  "Dark Khaki": "#bdb76b",
  "Dark Magenta": "#8b008b",
  "Dark Olive Green": "#556b2f",
  "Dark Orange": "#ff8c00",
  "Dark Orchid": "#9932cc",
  "Dark Red": "#8b0000",
  "Dark Salmon": "#e9967a",
  "Dark Sea Green": "#8fbc8f",
  "Dark Slate Blue": "#483d8b",
  "Dark Slate Grey": "#2f4f4f",
  "Dark Turquoise": "#00ced1",
  "Dark Violet": "#9400d3",
  "Deep Pink": "#ff1493",
  "Deep Sky Blue": "#00bfff",
  "Dim Grey": "#696969",
  "Dodger Blue": "#1e90ff",
  Firebrick: "#b22222",
  "Floral White": "#fffaf0",
  "Forest Green": "#228b22",
  Gainsboro: "#dcdcdc",
  "Ghost White": "#f8f8ff",
  Gold: "#ffd700",
  Goldenrod: "#daa520",
  Grey: "#808080",
  Green: "#008000",
  "Green Yellow": "#adff2f",
  Honeydew: "#f0fff0",
  "Hot Pink": "#ff69b4",
  "Indian Red": "#cd5c5c",
  Indigo: "#4b0082",
  Ivory: "#fffff0",
  Khaki: "#f0e68c",
  Lavender: "#e6e6fa",
  "Lavender Blush": "#fff0f5",
  "Lawn Green": "#7cfc00",
  "Lemon Chiffon": "#fffacd",
  "Light Blue": "#add8e6",
  "Light Coral": "#f08080",
  "Light Cyan": "#e0ffff",
  "Light Goldenrod Yellow": "#fafad2",
  "Light Grey": "#d3d3d3",
  "Light Green": "#90ee90",
  "Light Pink": "#ffb6c1",
  "Light Salmon": "#ffa07a",
  "Light Sea Green": "#20b2aa",
  "Light Sky Blue": "#87cefa",
  "Light Slate Grey": "#778899",
  "Light Steel Blue": "#b0c4de",
  "Light Yellow": "#ffffe0",
  Lime: "#00ff00",
  "Lime Green": "#32cd32",
  Linen: "#faf0e6",
  Magenta: "#ff00ff",
  Maroon: "#800000",
  "Medium Aquamarine": "#66cdaa",
  "Medium Blue": "#0000cd",
  "Medium Orchid": "#ba55d3",
  "Medium Purple": "#9370db",
  "Medium Sea Green": "#3cb371",
  "Medium Slate Blue": "#7b68ee",
  "Medium Spring Green": "#00fa9a",
  "Medium Turquoise": "#48d1cc",
  "Medium Violet Red": "#c71585",
  "Midnight Blue": "#191970",
  "Mint Cream": "#f5fffa",
  "Misty Rose": "#ffe4e1",
  Moccasin: "#ffe4b5",
  "Navajo White": "#ffdead",
  Navy: "#000080",
  "Old Lace": "#fdf5e6",
  Olive: "#808000",
  "Olive Drab": "#6b8e23",
  Orange: "#ffa500",
  "Orange Red": "#ff4500",
  Orchid: "#da70d6",
  "Pale Goldenrod": "#eee8aa",
  "Pale Green": "#98fb98",
  "Pale Turquoise": "#afeeee",
  "Pale Violet Red": "#db7093",
  "Papaya Whip": "#ffefd5",
  "Peach Puff": "#ffdab9",
  Peru: "#cd853f",
  Pink: "#ffc0cb",
  Plum: "#dda0dd",
  "Powder Blue": "#b0e0e6",
  Purple: "#800080",
  "Rebecca Purple": "#663399",
  Red: "#ff0000",
  "Rosy Brown": "#bc8f8f",
  "Royal Blue": "#4169e1",
  "Saddle Brown": "#8b4513",
  Salmon: "#fa8072",
  "Sandy Brown": "#f4a460",
  "Sea Green": "#2e8b57",
  Seashell: "#fff5ee",
  Sienna: "#a0522d",
  Silver: "#c0c0c0",
  "Sky Blue": "#87ceeb",
  "Slate Blue": "#6a5acd",
  "Slate Grey": "#708090",
  Snow: "#fffafa",
  "Spring Green": "#00ff7f",
  "Steel Blue": "#4682b4",
  Tan: "#d2b48c",
  Teal: "#008080",
  Thistle: "#d8bfd8",
  Tomato: "#ff6347",
  Turquoise: "#40e0d0",
  Violet: "#ee82ee",
  Wheat: "#f5deb3",
  White: "#ffffff",
  "White Smoke": "#f5f5f5",
  Yellow: "#ffff00",
  "Yellow Green": "#9acd32",
  // Apparel and retail colour names
  Amber: "#ffbf00",
  Apricot: "#fbceb1",
  "Ash Grey": "#b2beb5",
  "Baby Blue": "#89cff0",
  "Baby Pink": "#f4c2c2",
  "Blush Pink": "#fe828c",
  "Bottle Green": "#006a4e",
  "Brick Red": "#cb4154",
  Bronze: "#cd7f32",
  Burgundy: "#800020",
  Camel: "#c19a6b",
  "Canary Yellow": "#ffef00",
  Caramel: "#c68e17",
  Champagne: "#f7e7ce",
  Charcoal: "#36454f",
  "Cherry Red": "#d2042d",
  "Cobalt Blue": "#0047ab",
  Coffee: "#6f4e37",
  Copper: "#b87333",
  Cream: "#fffdd0",
  "Denim Blue": "#1560bd",
  "Dusty Rose": "#c9a9a6",
  Eggplant: "#614051",
  Emerald: "#50c878",
  Espresso: "#4e312d",
  Graphite: "#383838",
  Gunmetal: "#2a3439",
  "Jet Black": "#1c1c1c",
  "Metallic Gold": "#d4af37",
  "Lemon Yellow": "#fff44f",
  Lilac: "#c8a2c8",
  Mauve: "#e0b0ff",
  "Mehendi Green": "#6b7d3a",
  Mint: "#98ff98",
  Mustard: "#e1ad01",
  "Neon Green": "#39ff14",
  "Neon Pink": "#ff6ec7",
  Nude: "#e3bc9a",
  Oatmeal: "#e0dcc8",
  "Off White": "#faf9f6",
  "Olive Green": "#708238",
  "Parrot Green": "#12ad2b",
  Peach: "#ffcba4",
  "Peacock Blue": "#005f69",
  Periwinkle: "#ccccff",
  "Pista Green": "#93c572",
  Pumpkin: "#ff7518",
  "Rani Pink": "#e0115f",
  "Rose Gold": "#b76e79",
  Ruby: "#9b111e",
  Rust: "#b7410e",
  Saffron: "#f4c430",
  "Sage Green": "#9caf88",
  Sand: "#c2b280",
  Sapphire: "#0f52ba",
  "Steel Grey": "#71797e",
  "Stone Grey": "#928e85",
  Tangerine: "#f28500",
  Taupe: "#483c32",
  "Teal Blue": "#367588",
  Terracotta: "#e2725b",
  Walnut: "#773f1a",
  Wine: "#722f37",
};

type Lab = [number, number, number];

function hexToLab(hex: string): Lab {
  const n = parseInt(hex.slice(1), 16);
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const r = lin((n >> 16) & 255);
  const g = lin((n >> 8) & 255);
  const b = lin(n & 255);
  // sRGB (D65) → XYZ, normalised to the D65 white point
  const x = (0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / 0.95047;
  const y = 0.2126729 * r + 0.7151522 * g + 0.072175 * b;
  const z = (0.0193339 * r + 0.119192 * g + 0.9503041 * b) / 1.08883;
  const f = (t: number) => (t > 216 / 24389 ? Math.cbrt(t) : ((24389 / 27) * t + 16) / 116);
  const fx = f(x);
  const fy = f(y);
  const fz = f(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/** CIEDE2000 colour difference (kL = kC = kH = 1). */
export function deltaE2000([L1, a1, b1]: Lab, [L2, a2, b2]: Lab): number {
  const C1 = Math.hypot(a1, b1);
  const C2 = Math.hypot(a2, b2);
  const Cbar = (C1 + C2) / 2;
  const G = 0.5 * (1 - Math.sqrt(Cbar ** 7 / (Cbar ** 7 + 25 ** 7)));
  const a1p = (1 + G) * a1;
  const a2p = (1 + G) * a2;
  const C1p = Math.hypot(a1p, b1);
  const C2p = Math.hypot(a2p, b2);
  const h = (a: number, b: number) =>
    a === 0 && b === 0 ? 0 : (deg(Math.atan2(b, a)) + 360) % 360;
  const h1p = h(a1p, b1);
  const h2p = h(a2p, b2);
  const dLp = L2 - L1;
  const dCp = C2p - C1p;
  let dhp = 0;
  if (C1p * C2p !== 0) {
    dhp = h2p - h1p;
    if (dhp > 180) dhp -= 360;
    else if (dhp < -180) dhp += 360;
  }
  const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin(rad(dhp / 2));
  const Lbp = (L1 + L2) / 2;
  const Cbp = (C1p + C2p) / 2;
  let hbp = h1p + h2p;
  if (C1p * C2p !== 0) {
    if (Math.abs(h1p - h2p) > 180) hbp += h1p + h2p < 360 ? 360 : -360;
    hbp /= 2;
  }
  const T =
    1 -
    0.17 * Math.cos(rad(hbp - 30)) +
    0.24 * Math.cos(rad(2 * hbp)) +
    0.32 * Math.cos(rad(3 * hbp + 6)) -
    0.2 * Math.cos(rad(4 * hbp - 63));
  const dTheta = 30 * Math.exp(-(((hbp - 275) / 25) ** 2));
  const Rc = 2 * Math.sqrt(Cbp ** 7 / (Cbp ** 7 + 25 ** 7));
  const Sl = 1 + (0.015 * (Lbp - 50) ** 2) / Math.sqrt(20 + (Lbp - 50) ** 2);
  const Sc = 1 + 0.045 * Cbp;
  const Sh = 1 + 0.015 * Cbp * T;
  const Rt = -Math.sin(rad(2 * dTheta)) * Rc;
  return Math.sqrt(
    (dLp / Sl) ** 2 + (dCp / Sc) ** 2 + (dHp / Sh) ** 2 + Rt * (dCp / Sc) * (dHp / Sh),
  );
}

let table: { name: string; lab: Lab }[] | null = null;

/** The closest colour name for "#rrggbb" by ΔE00. */
export function nameForHex(hex: string): string {
  table ??= Object.entries(NAMES).map(([name, h]) => ({ name, lab: hexToLab(h) }));
  const lab = hexToLab(hex);
  let best = table[0]!;
  let bestD = Infinity;
  for (const row of table) {
    const d = deltaE2000(lab, row.lab);
    if (d < bestD) {
      bestD = d;
      best = row;
    }
  }
  return best.name;
}

/** The hex of a known colour name ("navy blue" → undefined; "Navy" → "#000080"). */
export function hexForName(name: string): string | undefined {
  const key = Object.keys(NAMES).find((k) => k.toLowerCase() === name.trim().toLowerCase());
  return key ? NAMES[key] : undefined;
}

/** A hex code typed as an option value: "#1f6f43", "1F6F43" or "#1f6" (the 3-digit form needs "#"). */
export function typedHex(value: string): string | null {
  const v = value.trim().toLowerCase();
  const m = /^#([0-9a-f]{3})$/.exec(v) ?? /^#?([0-9a-f]{6})$/.exec(v);
  if (!m) return null;
  const d = m[1]!;
  return d.length === 3 ? `#${[...d].map((c) => c + c).join("")}` : `#${d}`;
}

/** `name`, or "name 2", "name 3"… when the option already has it (case-insensitive). */
export function uniqueName(name: string, taken: string[]): string {
  const has = (n: string) => taken.some((x) => x.toLowerCase() === n.toLowerCase());
  if (!has(name)) return name;
  for (let i = 2; ; i++) if (!has(`${name} ${i}`)) return `${name} ${i}`;
}
